import React from 'react';
import { useSettingsQuery } from 'src/api/queries/userQueries';
import { PageIntro, PageTitle, SaveChangesBar } from 'src/components';
import styles from './styles.module.scss';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import ReactSwitch from 'react-switch';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { ACTION_TYPES } from 'src/shared/settings/base';
import { useAlerts } from 'src/common/AlertManager';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { updateAddonSettingsApi } from 'src/api/functions/userApi';
import SvgWrench from 'src/common/icons/Wrench';
import { ImageSquare, VideoCamera, PlayBox, Eye, PuzzlePiece, Crop, Printer, StatsCdn } from 'src/common/icons';
import FaceSearchIcon from 'src/common/icons/FaceSearch';
import SvgChatAI from 'src/common/icons/ChatAI';
import useModal from 'src/hooks/useModal';
import AddonSettingsModal from 'src/components/AddonSettingsModal';

// Full hardcoded addon catalog rendered on this page, in display order.
// Titles/descriptions mirror the DB seed (deploy/database/conf/05-init-data-load.sql
// and 19-print-export-addon.sql) and are used as fallback copy for addons the
// account does not have (the backend omits non-granted addons from the settings
// payload, so their title/description are not available client-side).
const ADDON_CATALOG: { key: string; title: string; description: string }[] = [
  {
    key: 'ON_DEMAND_IMAGE',
    title: 'On-demand image sizing with CDN delivery',
    description: 'On-demand image sizing with CDN delivery',
  },
  {
    key: 'VIDEO_TRANSCODE',
    title: 'Transcode Video to SD, HD and HLS videos',
    description: 'Transcode Video to SD, HD and HLS videos',
  },
  {
    key: 'STORYBOARD',
    title: 'Video Storyboard',
    description: 'Extract image frames from video as storyboard images',
  },
  {
    key: 'FACE_RECOGNITION',
    title: 'Face Recognition and Search',
    description: 'ML-based analysis and indexing of faces in images for search and discovery',
  },
  {
    key: 'IMAGE_ANALYSIS',
    title: 'Image auto-tags',
    description: 'ML-based tagging of images for search and discovery',
  },
  {
    key: 'REMOVE_BACKGROUND',
    title: 'Remove Image Background',
    description: 'Removes background from an image and replace with transparency',
  },
  {
    key: 'PRINT_EXPORT',
    title: 'Print Export (CMYK / 300 DPI)',
    description:
      'Export images as professional print files: CMYK colour, 300 DPI, TIFF / CMYK JPEG / print PDF / PDF/X-1a',
  },
  {
    key: 'AI_ASSISTANT',
    title: 'AI Agent',
    description: 'AI agent to execute complex workflows using natural language',
  },
  {
    key: 'AI_IMAGE_EDIT',
    title: 'AI Image Edit',
    description:
      'AI-powered photo editing and generation (enhancement, background removal, generative fill/expand, text-to-image)',
  },
  {
    key: 'DELIVERY_INSIGHTS',
    title: 'Delivery Insights',
    description: 'Analytics on asset delivery and CDN usage',
  },
];

const UNAVAILABLE_ADDON_MESSAGE = 'Get in touch with us to try these addons now.';

// Addons that expose a configurable trigger-criteria UI (gear icon).
// ODI is excluded per spec; VIDEO_TRANSCODE and STORYBOARD don't take criteria.
const CONFIGURABLE_ADDONS = new Set([
  'FACE_RECOGNITION',
  'IMAGE_ANALYSIS',
  'REMOVE_BACKGROUND',
  'PRINT_EXPORT',
]);

const ADDON_ICONS: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  ON_DEMAND_IMAGE: ImageSquare,
  VIDEO_TRANSCODE: VideoCamera,
  STORYBOARD: PlayBox,
  FACE_RECOGNITION: FaceSearchIcon,
  IMAGE_ANALYSIS: Eye,
  REMOVE_BACKGROUND: Crop,
  AI_ASSISTANT: SvgChatAI,
  AI_IMAGE_EDIT: ImageSquare,
  PRINT_EXPORT: Printer,
  DELIVERY_INSIGHTS: StatsCdn,
};

export const AddonsSettings = () => {
  const { isLoading } = useSettingsQuery();
  const { showSuccess, showAlert } = useAlerts();
  const accessToken = useAccessToken();
  const { showModal } = useModal();

  const [addonsSettings, updateAddonsSettings] = useAtom(addonsSettingsAtom);

  const addons = useMemo(() => {
    return ADDON_CATALOG.map(({ key, title, description }) => {
      const state: any = addonsSettings?.[key];
      return {
        key,
        title: state?.title || title,
        description: state?.description || description,
        added: Boolean(state?.added),
        enabled: Boolean(state?.enabled),
      };
    });
  }, [addonsSettings]);

  const addedAddons = useMemo(() => addons.filter((addon) => addon.added), [addons]);
  const availableAddons = useMemo(() => addons.filter((addon) => !addon.added), [addons]);

  if (isLoading) return null;

  const handleAddonUpdate = (addon: string, value: any) =>
    updateAddonsSettings({
      type: 'updateState',
      settings: 'addons_settings',
      payload: {
        [addon]: {
          enabled: value,
        },
      },
    });

  const disabledKeys = ['ON_DEMAND_IMAGE', 'VIDEO_TRANSCODE', 'IMAGE_DEEPZOOM'];

  const saveAddonSettings = async (addon: string, eventHooks: any) => {
    const data = { 'event-hooks': eventHooks };
    const value = await updateAddonSettingsApi(data, addon, accessToken);
    if (value?.error) {
      showAlert(`Failed to update settings`);
      throw new Error('update failed');
    }
    showSuccess(`Settings Updated`);
    updateAddonsSettings({
      type: ACTION_TYPES.UPDATE_STATE,
      payload: {
        [addon]: {
          settings: data,
        },
      },
    });
  };

  const openSettingsModal = (addon: { key: string; title?: string }) => {
    const initialEventHooks = addonsSettings[addon.key]?.settings?.['event-hooks'];
    showModal(
      <AddonSettingsModal
        addonKey={addon.key}
        addonTitle={addon.title}
        initialEventHooks={initialEventHooks}
        onSave={(eventHooks) => saveAddonSettings(addon.key, eventHooks)}
      />,
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <PageTitle title="Addons" className={styles.pageTitle} />
      <SaveChangesBar />

      <PageIntro
        inset
        overview={(
          <>
            Work your assets do for themselves — cutting out backgrounds, reading
            what is in an image, building print-ready files, recognising faces —
            so your team is not doing it by hand in another tool and uploading the
            result back.
          </>
        )}
        points={[
          {
            label: 'Runs on a trigger, not on everything',
            text: 'set a condition and the work fires only where it belongs — build the print-ready export when an asset is approved, rather than on every file anyone uploads.',
          },
          {
            label: 'Only on relevant assets',
            text: 'conditions match on your own metadata, so a rule can apply to one campaign, one product line or one shot type.',
          },
          {
            label: 'Costs follow the trigger',
            text: 'narrower conditions mean less processing billed, and no queue full of work nobody asked for.',
          },
        ]}
        footnote="Anything with a gear icon takes trigger conditions. Everything here can be switched on or off at any time."
      />

      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.addonCardGrid}>
            {addedAddons.map((addon) => {
              const IconComponent = ADDON_ICONS[addon.key] || PuzzlePiece;
              return (
                <div key={addon.key} className={styles.addonCard}>
                  <div className={styles.addonCardIcon}>
                    <IconComponent />
                  </div>
                  <div className={styles.addonCardInfo}>
                    <h4>{addon.title}</h4>
                    {addon.description && <p>{addon.description}</p>}
                  </div>
                  <div className={styles.addonCardActions}>
                    <ReactSwitch
                      checked={addon.enabled}
                      onChange={(checked: boolean) => handleAddonUpdate(addon.key, checked)}
                      checkedIcon={false}
                      uncheckedIcon={false}
                      disabled={disabledKeys.includes(addon.key)}
                      onColor="#2e53e3"
                      width={40}
                      height={20}
                      handleDiameter={16}
                    />
                    {CONFIGURABLE_ADDONS.has(addon.key) && (
                      <div className={styles.gearHolder}>
                        <SvgWrench
                          style={{ cursor: 'pointer', color: '#000' }}
                          onClick={() => openSettingsModal(addon)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {availableAddons.length > 0 && (
            <div className={styles.availableSection}>
              <h3 className={styles.availableHeading}>Available Addons</h3>
              <p className={styles.availableMessage}>{UNAVAILABLE_ADDON_MESSAGE}</p>
              <div className={styles.addonCardGrid}>
                {availableAddons.map((addon) => {
                  const IconComponent = ADDON_ICONS[addon.key] || PuzzlePiece;
                  return (
                    <div key={addon.key} className={`${styles.addonCard} ${styles.addonCardUnavailable}`}>
                      <div className={styles.addonCardIcon}>
                        <IconComponent />
                      </div>
                      <div className={styles.addonCardInfo}>
                        <h4>{addon.title}</h4>
                        {addon.description && <p>{addon.description}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
